import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from '../ui/sheet';
import { cn } from '@/lib/utils';
import {
  Store,
  CreditCard,
  DollarSign,
  Percent,
  Star,
  Tag,
  Printer,
  Globe,
  FileText,
  Database,
  Building2,
  Plus,
  Menu,
  Settings,
  Download,
} from 'lucide-react';

// Menu items configuration
export const getSettingsMenuItems = (t, canManageOutlets, isOwner = false) => {
  const items = [
    {
      id: 'outlet',
      label: t('settings.tabs.outlet'),
      icon: Store,
      testId: 'settings-nav-outlet',
    },
    {
      id: 'payment',
      label: t('settings.tabs.payment'),
      icon: CreditCard,
      testId: 'settings-nav-payment',
    },
  ];

  if (canManageOutlets) {
    items.push(
      {
        id: 'tax_currency',
        label: t('settings.tabs.tax_currency'),
        icon: DollarSign,
        testId: 'settings-nav-tax-currency',
      },
      {
        id: 'discounts',
        label: t('settings.tabs.discounts'),
        icon: Percent,
        testId: 'settings-nav-discounts',
      },
      {
        id: 'loyalty',
        label: t('settings.tabs.loyalty'),
        icon: Star,
        testId: 'settings-nav-loyalty',
      },
      {
        id: 'tags',
        label: t('settings.tabs.tags'),
        icon: Tag,
        testId: 'settings-nav-tags',
      },
      {
        id: 'printers',
        label: t('settings.tabs.printers'),
        icon: Printer,
        testId: 'settings-nav-printers',
      }
    );
  }

  items.push({
    id: 'language',
    label: t('settings.tabs.language'),
    icon: Globe,
    testId: 'settings-nav-language',
  });

  if (canManageOutlets) {
    items.push(
      {
        id: 'translations',
        label: t('settings.tabs.translations') || 'Translations',
        icon: FileText,
        testId: 'settings-nav-translations',
      },
      {
        id: 'backup',
        label: 'Backup',
        icon: Database,
        testId: 'settings-nav-backup',
      },
      {
        id: 'outlets',
        label: t('settings.tabs.all_outlets'),
        icon: Building2,
        testId: 'settings-nav-outlets',
      },
      {
        id: 'new',
        label: t('settings.tabs.new_outlet'),
        icon: Plus,
        testId: 'settings-nav-new',
      }
    );
  }

  // Update System - OWNER only
  if (isOwner) {
    items.push({
      id: 'update',
      label: 'Update System',
      icon: Download,
      testId: 'settings-nav-update',
    });
  }

  return items;
};

// Menu item component
const MenuItem = ({ item, isActive, onClick }) => {
  const Icon = item.icon;
  
  return (
    <button
      onClick={() => onClick(item.id)}
      data-testid={item.testId}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
        'hover:bg-accent/80',
        isActive && 'bg-primary/10 text-primary border-l-2 border-primary -ml-[2px] pl-[14px]',
        !isActive && 'text-muted-foreground hover:text-foreground'
      )}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0', isActive && 'text-primary')} />
      <span className="truncate">{item.label}</span>
    </button>
  );
};

// Desktop Sidebar
export const SettingsSidebar = ({ 
  activeTab, 
  onTabChange, 
  menuItems 
}) => {
  return (
    <aside 
      className="hidden lg:flex flex-col w-56 border-r bg-card/50 shrink-0"
      data-testid="settings-sidebar"
    >
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-primary" />
          <span className="font-semibold text-sm">Pengaturan</span>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <nav className="p-3 space-y-1">
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              isActive={activeTab === item.id}
              onClick={onTabChange}
            />
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
};

// Mobile Sidebar (Sheet/Drawer)
export const MobileSettingsSidebar = ({ 
  activeTab, 
  onTabChange, 
  menuItems,
  open,
  onOpenChange
}) => {
  const handleItemClick = (tabId) => {
    onTabChange(tabId);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="lg:hidden"
          data-testid="settings-mobile-menu-btn"
        >
          <Menu className="h-4 w-4 mr-2" />
          Menu Pengaturan
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="flex items-center gap-2 text-left">
            <Settings className="h-5 w-5 text-primary" />
            <span>Pengaturan</span>
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-80px)]">
          <nav className="p-3 space-y-1">
            {menuItems.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                isActive={activeTab === item.id}
                onClick={handleItemClick}
              />
            ))}
          </nav>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// Get current menu title
export const getActiveMenuTitle = (menuItems, activeTab) => {
  const item = menuItems.find(m => m.id === activeTab);
  return item ? item.label : '';
};
